import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UsersThree, Plus, Copy, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTranslation } from "@/contexts/LanguageContext";

export default function ClassManager({ classes, setClasses, classId, setClassId }) {
  const { lang } = useTranslation();
  const es = lang === "es";
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => axios.get("/api/admin/classes", { withCredentials: true }).then((r) => setClasses(r.data.classes)).catch(() => {});
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post("/api/admin/classes", { name }, { withCredentials: true });
      setClasses((c) => [...c, data]);
      setName("");
      toast.success(es ? `Clase creada · código ${data.code}` : `Class created · code ${data.code}`);
    } catch {
      toast.error(es ? "No se pudo crear la clase" : "Couldn't create the class");
    }
    setBusy(false);
  };

  const remove = async (c) => {
    if (!window.confirm(es ? `¿Eliminar "${c.name}"? Los estudiantes quedarán sin clase (su progreso no se borra).` : `Delete "${c.name}"? Students will be left without a class (their progress is kept).`)) return;
    await axios.delete(`/api/admin/classes/${c.class_id}`, { withCredentials: true });
    if (classId === c.class_id) setClassId("");
    load();
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(es ? "Código copiado" : "Code copied");
    } catch {
      toast.message(code);
    }
  };

  const pill = (active) => `px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-[#F5A524] text-[#0A0A0A] border-[#F5A524]" : "border-white/15 text-[#D4D4D8] hover:bg-white/5"}`;

  return (
    <section className="bg-[#121214]/80 border border-white/10 rounded-xl p-4 sm:p-5 space-y-4" data-testid="class-manager">
      <div>
        <h2 className="flex items-center gap-2 font-['Outfit'] text-white text-lg font-semibold">
          <UsersThree weight="fill" className="w-5 h-5 text-[#F5A524]" />
          {es ? "Clases" : "Classes"}
        </h2>
        <p className="text-[#71717A] text-sm">
          {es ? "Crea una clase y comparte su código. Los estudiantes lo escriben una vez en Aprender → Cuestionarios." : "Create a class and share its code. Students type it once in Learn → Quizzes."}
        </p>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="class-filter">
        <button type="button" className={pill(!classId)} onClick={() => setClassId("")} data-testid="class-filter-all">
          {es ? "Todos los estudiantes" : "All students"}
        </button>
        {classes.map((c) => (
          <button type="button" key={c.class_id} className={pill(classId === c.class_id)} onClick={() => setClassId(c.class_id)} data-testid={`class-filter-${c.code}`}>
            {c.name} <span className="font-['JetBrains_Mono'] opacity-70">· {c.students}</span>
          </button>
        ))}
      </div>
      {classes.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {classes.map((c) => (
            <div key={c.class_id} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2" data-testid={`class-row-${c.code}`}>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{c.name}</p>
                <p className="text-[#71717A] text-xs">{c.students} {es ? (c.students === 1 ? "estudiante" : "estudiantes") : c.students === 1 ? "student" : "students"}</p>
              </div>
              <button type="button" onClick={() => copy(c.code)} className="font-['JetBrains_Mono'] text-[#F5A524] text-sm flex items-center gap-1 hover:underline" data-testid={`class-code-${c.code}`}>
                {c.code}
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button type="button" onClick={() => remove(c)} className="text-[#71717A] hover:text-red-400 p-1" title={es ? "Eliminar" : "Delete"} data-testid={`class-delete-${c.code}`}>
                <Trash className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 max-w-md">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} placeholder={es ? "Nombre de la clase (ej. Sábados 10am)" : "Class name (e.g. Saturday 10am)"} className="bg-[#0A0A0A] border-white/10 text-white" data-testid="class-name-input" />
        <Button onClick={create} disabled={busy || !name.trim()} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] shrink-0" data-testid="class-create-btn">
          <Plus weight="bold" className="w-4 h-4 mr-1" />
          {es ? "Crear" : "Create"}
        </Button>
      </div>
    </section>
  );
}
